<template>
  <section class="signals-card" aria-label="Signals">
    <header class="signals-card__header">
      <h3 class="signals-card__title">Signals</h3>
      <p class="signals-card__subtitle">
        What changed in how this account uses exIQtive, and what to do about it.
      </p>
    </header>

    <div v-if="groups.length === 0" class="signals-card__empty">
      No signals for this account right now.
    </div>

    <div v-else class="signals-card__groups">
      <div v-for="group in groups" :key="group.name" class="signals-card__group-block">
        <div class="signals-card__group">
          <span class="signals-card__group-name">{{ group.name }}</span>
          <span
            v-if="group.status_badge"
            class="signals-card__group-badge"
            :class="`signals-card__group-badge--${bandClass(group.status_badge)}`"
          >
            {{ group.status_badge }}
          </span>
        </div>

        <article
          v-for="signal in group.signals"
          :key="`${group.name}-${signal.title}`"
          class="signals-card__signal"
        >
          <span
            class="signals-card__featured-icon"
            :class="`signals-card__featured-icon--${signal.severity}`"
            aria-hidden="true"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                v-if="signal.severity === 'critical'"
                d="M10 6V11M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
                :stroke="severityStroke(signal.severity)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                v-else-if="signal.severity === 'warning'"
                d="M10 7V12M10 15H10.01M8.575 2.867l-6.9 11.4A1.5 1.5 0 0 0 2.975 16.5h13.8a1.5 1.5 0 0 0 1.3-2.233l-6.9-11.4a1.5 1.5 0 0 0-2.6 0z"
                :stroke="severityStroke(signal.severity)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                v-else
                d="M7 10.5L9.5 13L14 7M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
                :stroke="severityStroke(signal.severity)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>

          <div class="signals-card__signal-text">
            <h4 class="signals-card__signal-title">{{ signal.title }}</h4>
            <p class="signals-card__signal-description">{{ signal.description }}</p>
            <p v-if="signal.do_next" class="signals-card__signal-do-next">
              Do next: {{ signal.do_next }}
            </p>
          </div>

          <a
            v-if="signal.playbook_url"
            class="signals-card__playbook"
            :href="signal.playbook_url"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open playbook
          </a>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup>
import { defineProps } from "vue";

defineProps({
  groups: {
    type: Array,
    default: () => [],
  },
});

const SEVERITY_STROKES = {
  critical: "#d92d20",
  warning: "#dc6803",
  positive: "#079455",
};

function severityStroke(severity) {
  return SEVERITY_STROKES[severity] || SEVERITY_STROKES.positive;
}

function bandClass(badge) {
  const raw = String(badge || "").toLowerCase();
  if (raw === "sticky") return "sticky";
  if (raw === "stalling") return "stalling";
  return "watch";
}
</script>

<style scoped>
.signals-card {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  overflow: hidden;
}

.signals-card__header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 20px;
}

.signals-card__title {
  margin: 0;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.signals-card__subtitle {
  margin: 0;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.signals-card__empty {
  padding: 16px 20px;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
}

.signals-card__group {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #f9fafb;
  border-top: 1px solid #eaecf0;
  border-bottom: 1px solid #eaecf0;
}

.signals-card__group-name {
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.signals-card__group-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.signals-card__group-badge--sticky {
  color: #067647;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}

.signals-card__group-badge--watch {
  color: #b54708;
  background: #fffaeb;
  border: 1px solid #fedf89;
}

.signals-card__group-badge--stalling {
  color: #b42318;
  background: #fee4e2;
  border: 1px solid #fecdca;
}

.signals-card__signal {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #eaecf0;
}

.signals-card__signal:last-child {
  border-bottom: none;
}

.signals-card__featured-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 9999px;
}

.signals-card__featured-icon--critical {
  background: #fee4e2;
}

.signals-card__featured-icon--warning {
  background: #fef0c7;
}

.signals-card__featured-icon--positive {
  background: #ecfdf3;
}

.signals-card__signal-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.signals-card__signal-title {
  margin: 0;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.signals-card__signal-description {
  margin: 0;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.signals-card__signal-do-next {
  margin: 0;
  color: #6b21a8;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.signals-card__playbook {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding: 10px 16px;
  border-radius: 8px;
  background: #f2edff;
  border: 1px solid #f2edff;
  color: #6941c6;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-decoration: none;
  white-space: nowrap;
}

.signals-card__playbook:hover {
  color: #53389e;
  text-decoration: none;
}

.signals-card__playbook:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}
</style>
